import type { FieldType } from "../../types";
import { array, check, literal, minLength, object, pipe, string, variant } from "valibot";
import {
  fieldValueReferenceOfTypes,
  metaVariableValueReferenceOfTypes,
  variableValueReferenceOfTypes,
} from "./references";
import { errorTranslation } from "./base";
import type { VariableType } from "../../variables/types";
import type { Delimiter } from "../stringTemplateUtil";
import { type MetaVariableId } from "../../metaVariables/metaVariable";
import { keyFromSelector } from "i18next";

export const stringTemplate = (
  allowedFieldTypes: FieldType[],
  allowedVariableTypes: VariableType[],
  allowedMetaVariables: MetaVariableId[],
  max: number,
  required = true,
  delimiter: Delimiter = "${",
  allowedFieldOptions?: Partial<Record<FieldType, Record<string, unknown>>>,
) =>
  pipe(
    array(
      variant("type", [
        object({
          type: literal("literal"),
          value: pipe(
            string(),
            check(
              (literalValue) => !literalValue.includes(delimiter),
              errorTranslation(keyFromSelector(($) => $.VALIDATION_INVALID_TEMPLATE_SEGMENT)),
            ),
          ),
        }),
        object({
          type: literal("field"),
          value: fieldValueReferenceOfTypes(allowedFieldTypes, allowedFieldOptions),
        }),
        object({
          type: literal("variable"),
          value: variableValueReferenceOfTypes(allowedVariableTypes),
        }),
        object({ type: literal("metaVariable"), value: metaVariableValueReferenceOfTypes(allowedMetaVariables) }),
        object({ type: literal("unknown"), value: string() }),
      ]),
    ),
    minLength(required ? 1 : 0, errorTranslation(keyFromSelector(($) => $.VALIDATION_NOT_EMPTY))),
    check(
      (value) => !value.some(({ type }) => type === "unknown"),
      errorTranslation(keyFromSelector(($) => $.VALIDATION_FIELD_REFERENCE_DOES_NOT_EXIST)),
    ),
    check(
      (value) =>
        value.reduce((sum, { type, value }) => {
          switch (type) {
            case "literal":
              return sum + value.length;
            case "field":
            case "variable":
            case "metaVariable":
              return sum + 1;
            case "unknown":
              return sum;
          }
        }, 0) <= max,
      errorTranslation(
        keyFromSelector(($) => $.VALIDATION_MAX_LENGTH),
        { expected: max },
      ),
    ),
  );
